import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileUpload } from "../FileUpload";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

const ChecklistEditor = ({ items, setItems, orderId }) => {
  const update = (i, patch) => setItems(items.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const add = () => setItems([...items, { key: `manual.${Date.now()}`, label: "", requirement: "", source: "manual", result: "PENDING", measured_value: "", unit: "", remarks: "" }]);
  return (
    <div className="space-y-2" data-testid={`admin-quality-checklist-editor-${orderId}`}>
      {items.map((c, i) => (
        <div key={c.key} className="grid grid-cols-2 lg:grid-cols-12 gap-2 p-3 rounded-lg bg-card border border-border items-end" data-testid={`admin-quality-item-${orderId}-${c.key}`}>
          <div className="col-span-2 lg:col-span-3 space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Item {c.source !== "manual" && <span className="normal-case">· from {c.source}</span>}</Label>
            <Input value={c.label} onChange={(e) => update(i, { label: e.target.value })} readOnly={c.source !== "manual"} data-testid={`admin-quality-item-label-${orderId}-${c.key}`} />
          </div>
          <div className="col-span-2 lg:col-span-3 space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Requirement</Label>
            <Input value={c.requirement} onChange={(e) => update(i, { requirement: e.target.value })} data-testid={`admin-quality-item-req-${orderId}-${c.key}`} />
          </div>
          <div className="space-y-1 lg:col-span-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Measured</Label>
            <Input value={c.measured_value} onChange={(e) => update(i, { measured_value: e.target.value })} data-testid={`admin-quality-item-measured-${orderId}-${c.key}`} />
          </div>
          <div className="space-y-1 lg:col-span-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Unit</Label>
            <Input value={c.unit} onChange={(e) => update(i, { unit: e.target.value })} data-testid={`admin-quality-item-unit-${orderId}-${c.key}`} />
          </div>
          <div className="space-y-1 lg:col-span-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Result</Label>
            <Select value={c.result} onValueChange={(v) => update(i, { result: v })}>
              <SelectTrigger data-testid={`admin-quality-item-result-${orderId}-${c.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["PENDING", "PASS", "FAIL", "NA"].map((r) => <SelectItem key={r} value={r} data-testid={`admin-quality-item-result-${orderId}-${c.key}-${r.toLowerCase()}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 lg:col-span-2 space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Remarks</Label>
            <Input value={c.remarks} onChange={(e) => update(i, { remarks: e.target.value })} data-testid={`admin-quality-item-remarks-${orderId}-${c.key}`} />
          </div>
          <div className="lg:col-span-1 flex justify-end">
            {c.source === "manual" && <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`admin-quality-item-remove-${orderId}-${c.key}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>}
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="rounded-full" data-testid={`admin-quality-add-item-${orderId}`}><Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add checklist item</Button>
    </div>
  );
};

export const QualityPanel = ({ order, onUpdated }) => {
  const current = order.quality?.inspections?.slice(-1)[0];
  const [items, setItems] = useState(current?.status === "IN_PROGRESS" ? current.checklist : []);
  const [result, setResult] = useState("PASS");
  const [remarks, setRemarks] = useState("");
  const [customerRemarks, setCustomerRemarks] = useState("");
  const [files, setFiles] = useState([]);
  const [visible, setVisible] = useState({});
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onUpdated(data);
      toast.success(ok);
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  if (order.status === "QUALITY_PENDING") {
    return (
      <Button size="sm" disabled={busy} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/quality/start`, { note: "" }), "Inspection started — checklist seeded from approved part data.")} className="rounded-full" data-testid={`admin-quality-start-btn-${order.id}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <ClipboardCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Start inspection
      </Button>
    );
  }

  if (order.status === "INSPECTION_IN_PROGRESS") {
    const complete = () => run(() => adminApi.post(`/admin/orders/${order.id}/quality/complete`, {
      checklist: items, result, remarks, customer_remarks: customerRemarks,
      evidence_file_ids: files.map((f) => f.id), customer_visible_file_ids: files.filter((f) => visible[f.id]).map((f) => f.id),
    }), `Inspection recorded: ${result}.`);
    return (
      <div className="w-full mt-2 p-5 rounded-lg bg-muted/40 border border-border space-y-4" data-testid={`admin-quality-panel-${order.id}`}>
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Inspection cycle {current?.cycle} · checklist seeded from approved part data — edit, add, then record</p>
        <ChecklistEditor items={items} setItems={setItems} orderId={order.id} />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Internal remarks (admin only)</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid={`admin-quality-remarks-${order.id}`} /></div>
          <div className="space-y-2"><Label>Customer-visible remarks</Label><Textarea rows={2} value={customerRemarks} onChange={(e) => setCustomerRemarks(e.target.value)} data-testid={`admin-quality-customer-remarks-${order.id}`} /></div>
        </div>
        <FileUpload files={files} onChange={setFiles} maxFiles={20} testId={`admin-quality-evidence-upload-${order.id}`} />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-3 text-xs" data-testid={`admin-quality-visibility-${order.id}`}>
            {files.map((f) => (
              <label key={f.id} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!visible[f.id]} onChange={(e) => setVisible({ ...visible, [f.id]: e.target.checked })} data-testid={`admin-quality-visible-${order.id}-${f.id}`} /> Show “{f.name}” to customer
              </label>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={result} onValueChange={setResult}>
            <SelectTrigger className="w-36" data-testid={`admin-quality-result-${order.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PASS" data-testid={`admin-quality-result-pass-${order.id}`}>PASS</SelectItem>
              <SelectItem value="FAIL" data-testid={`admin-quality-result-fail-${order.id}`}>FAIL</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" disabled={busy} onClick={complete} className="rounded-full" data-testid={`admin-quality-complete-btn-${order.id}`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <ClipboardCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Record inspection
          </Button>
        </div>
      </div>
    );
  }

  if (order.status === "QUALITY_FAILED") {
    return (
      <div className="w-full mt-2 p-5 rounded-lg bg-orange-500/5 border border-orange-500/20 space-y-3" data-testid={`admin-quality-rework-panel-${order.id}`}>
        <Label>Rework instructions for the assigned partner</Label>
        <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Re-bore to ±0.05 mm; re-inspect rejected pieces..." data-testid={`admin-quality-rework-input-${order.id}`} />
        <Button size="sm" disabled={busy || instructions.trim().length < 3} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/quality/rework`, { instructions }), "Rework requested — partner notified in their portal.")} className="rounded-full" data-testid={`admin-quality-rework-btn-${order.id}`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <RotateCcw className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Request rework
        </Button>
      </div>
    );
  }
  return null;
};
